import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { trainingsApi, type TrainingCompany } from '../services/trainingsApi';
import { Building2, ChevronDown } from 'lucide-react';

// ─── Context ────────────────────────────────────────────────────────

interface CompanyContextValue {
  selectedCompanyId: number | null; // null = all companies
  setSelectedCompanyId: (id: number | null) => void;
  companies: TrainingCompany[];
  selectedCompany: TrainingCompany | null;
  loading: boolean;
}

const STORAGE_KEY = 'crm_training_company';

function loadCompanyId(): number | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && saved !== 'null') {
      const n = parseInt(saved, 10);
      if (!isNaN(n)) return n;
    }
  } catch { /* ignore */ }
  return null;
}

const CompanyContext = createContext<CompanyContextValue>({
  selectedCompanyId: null,
  setSelectedCompanyId: () => {},
  companies: [],
  selectedCompany: null,
  loading: true,
});

export const CompanyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedCompanyId, setId] = useState<number | null>(loadCompanyId);
  const [companies, setCompanies] = useState<TrainingCompany[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('grant_crm_token');
    if (!token) {
      setLoading(false);
      return;
    }
    trainingsApi.getCompanies()
      .then(setCompanies)
      .catch(() => {}) // silently fail if not logged in
      .finally(() => setLoading(false));
  }, []);

  const setSelectedCompanyId = useCallback((id: number | null) => {
    setId(id);
    localStorage.setItem(STORAGE_KEY, String(id));
  }, []);

  const selectedCompany = companies.find(c => c.id === selectedCompanyId) || null;

  return (
    <CompanyContext.Provider value={{ selectedCompanyId, setSelectedCompanyId, companies, selectedCompany, loading }}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompanyContext = () => useContext(CompanyContext);

// ─── CompanySwitcher Widget ─────────────────────────────────────────

const COMPANY_COLORS: Record<string, string> = {
  'Strateg': 'bg-blue-500',
  'MIZiP': 'bg-green-500',
  'IRNT': 'bg-amber-500',
};

export const CompanySwitcher: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { selectedCompanyId, setSelectedCompanyId, companies, selectedCompany } = useCompanyContext();
  const [open, setOpen] = useState(false);

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Wybierz firmę szkoleniową"
      >
        <Building2 size={16} className="text-gray-500" />
        {selectedCompany ? (
          <span className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${COMPANY_COLORS[selectedCompany.short_name] || 'bg-gray-400'}`} />
            {selectedCompany.short_name}
          </span>
        ) : (
          <span className="text-gray-600">Wszystkie firmy</span>
        )}
        <ChevronDown size={14} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label="Lista firm szkoleniowych"
          className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1"
        >
          <li
            role="option"
            aria-selected={selectedCompanyId === null}
            onClick={() => { setSelectedCompanyId(null); setOpen(false); }}
            className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 flex items-center gap-2 ${
              selectedCompanyId === null ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
            }`}
          >
            <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
            Wszystkie firmy
          </li>
          {companies.map(c => (
            <li
              key={c.id}
              role="option"
              aria-selected={selectedCompanyId === c.id}
              onClick={() => { setSelectedCompanyId(c.id); setOpen(false); }}
              className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 flex items-center gap-2 ${
                selectedCompanyId === c.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
              }`}
            >
              <span className={`w-2.5 h-2.5 rounded-full ${COMPANY_COLORS[c.short_name] || 'bg-gray-400'}`} />
              {c.short_name}
              <span className="text-xs text-gray-400 ml-auto">{c.nip}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
